import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { ClientIdType } from "../types";
export interface ApiConfig {
  baseUrl: string;
  authToken: string;
  clientId?: ClientIdType;
  customFilters?: customFilter
}

export enum ConfigurationKeys {
  getR2DCustomerFacets = "getR2DCustomerFacets",
  getR2DSupplierFacets = "getR2DSupplierFacets",
  getR2DOrdersForSupplier = "getR2DOrdersForSupplier",
  getDispatchCustomerFacets = "getDispatchCustomerFacets",
  getDispatchSupplierFacets = "getDispatchSupplierFacets",
  getDispatchOrdersForSupplier = "getDispatchOrdersForSupplier",
  getDispatchRiderFacets = "getDispatchRiderFacets",
}

export type customFilter = Partial<{
  [key in ConfigurationKeys]: {
    endpoint?: string;
    filters?: {
      [key: string]: any;
    };
  };
}>;

const ApiConfigContext = createContext<ApiConfig | undefined>(undefined);

export interface ApiConfigProviderProps {
  children?: React.ReactNode;
  baseUrl: string;
  authToken: string;
  clientId?: ClientIdType;
  customFilters?: customFilter
}

let globalApiConfig: ApiConfig | null = null;

export const setGlobalApiConfig = (config: ApiConfig) => {
  globalApiConfig = config;
};

export const getGlobalApiConfig = (): ApiConfig | null => {
  return globalApiConfig;
};

export const ApiConfigProvider: React.FC<ApiConfigProviderProps> = ({
  children,
  baseUrl,
  authToken,
  clientId,
  customFilters
}) => {
  const config: ApiConfig = useMemo(
    () => ({
      baseUrl,
      authToken,
      clientId,
      customFilters,
    }),
    [baseUrl, authToken, clientId, customFilters],
  );

  // Commit-phase only — do not mutate shared API config during render.
  // Dashboards also call setGlobalApiConfig + readiness guards in their effects.
  useEffect(() => {
    setGlobalApiConfig(config);
  }, [config]);

  return (
    <ApiConfigContext.Provider value={config}>
      {children}
    </ApiConfigContext.Provider>
  );
};

export const useApiConfig = (): ApiConfig => {
  const context = useContext(ApiConfigContext);
  if (context === undefined) {
    throw new Error('useApiConfig must be used within an ApiConfigProvider');
  }
  return context;
};
